import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";

// Landing page for the post-signup "Save my site" handoff from the
// protopal.ai Try-it wizard. The accounts SPA redirects authenticated
// users here as /claim?job=<anonJobId>. We POST /api/try/claim with
// that job ID; the anon_try_session cookie (path=/api/try) rides
// along automatically and proves we're the same browser that ran the
// anon generation. On success we replace history with the new site's
// dashboard route.

type Phase = "claiming" | "ok" | "missing" | "expired" | "conflict" | "error";

export default function Claim() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>("claiming");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const jobId = params.get("job");

  useEffect(() => {
    let cancelled = false;
    if (!jobId) {
      setPhase("missing");
      return;
    }

    (async () => {
      try {
        const result = await api.claimAnonTry(jobId);
        if (cancelled) return;
        setPhase("ok");
        navigate(`/sites/${result.siteId}`, { replace: true });
      } catch (err: any) {
        if (cancelled) return;
        const status = err?.status as number | undefined;
        if (status === 404) setPhase("missing");
        else if (status === 410) setPhase("expired");
        else if (status === 403 || status === 409) setPhase("conflict");
        else {
          setErrorMsg(err?.payload?.error || err?.message || "Could not claim the site.");
          setPhase("error");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [jobId, navigate]);

  return (
    <div style={{ maxWidth: 520, margin: "60px auto", padding: 24 }}>
      <div style={{ borderRadius: 16, border: "1px solid #e2e8f0", padding: 32, background: "white" }}>
        {phase === "claiming" ? (
          <>
            <h1 style={{ fontSize: 22, fontWeight: 600 }}>Saving your site…</h1>
            <p style={{ marginTop: 8, color: "#64748b" }}>
              Almost there. Linking the draft you generated to your new account.
            </p>
          </>
        ) : null}

        {phase === "ok" ? (
          <>
            <h1 style={{ fontSize: 22, fontWeight: 600 }}>Saved.</h1>
            <p style={{ marginTop: 8, color: "#64748b" }}>Taking you to your dashboard…</p>
          </>
        ) : null}

        {phase === "missing" ? (
          <>
            <h1 style={{ fontSize: 22, fontWeight: 600 }}>We couldn't find that draft</h1>
            <p style={{ marginTop: 8, color: "#64748b" }}>
              The link may be wrong, or you signed in on a different browser than the one that generated
              the draft. You can start a fresh site from your dashboard.
            </p>
            <Link
              to="/dashboard"
              style={{
                marginTop: 20,
                display: "inline-block",
                padding: "10px 18px",
                borderRadius: 999,
                background: "#36D6B5",
                color: "#0A1427",
                fontWeight: 600,
              }}
            >
              Go to dashboard
            </Link>
          </>
        ) : null}

        {phase === "expired" ? (
          <>
            <h1 style={{ fontSize: 22, fontWeight: 600 }}>This draft expired</h1>
            <p style={{ marginTop: 8, color: "#64748b" }}>
              Anonymous drafts are held for 24 hours. You can start a new one any time.
            </p>
            <Link
              to="/start"
              style={{
                marginTop: 20,
                display: "inline-block",
                padding: "10px 18px",
                borderRadius: 999,
                background: "#36D6B5",
                color: "#0A1427",
                fontWeight: 600,
              }}
            >
              Start a new site
            </Link>
          </>
        ) : null}

        {phase === "conflict" ? (
          <>
            <h1 style={{ fontSize: 22, fontWeight: 600 }}>Already claimed</h1>
            <p style={{ marginTop: 8, color: "#64748b" }}>
              This draft has already been saved to another account.
            </p>
            <Link
              to="/dashboard"
              style={{
                marginTop: 20,
                display: "inline-block",
                padding: "10px 18px",
                borderRadius: 999,
                background: "#36D6B5",
                color: "#0A1427",
                fontWeight: 600,
              }}
            >
              Go to dashboard
            </Link>
          </>
        ) : null}

        {phase === "error" ? (
          <>
            <h1 style={{ fontSize: 22, fontWeight: 600 }}>Something went wrong</h1>
            <p style={{ marginTop: 8, color: "#64748b" }}>
              {errorMsg || "We couldn't link your draft right now."}
            </p>
            <Link
              to="/dashboard"
              style={{
                marginTop: 20,
                display: "inline-block",
                padding: "10px 18px",
                borderRadius: 999,
                background: "#36D6B5",
                color: "#0A1427",
                fontWeight: 600,
              }}
            >
              Continue to dashboard
            </Link>
          </>
        ) : null}
      </div>
    </div>
  );
}
